import { useEffect, useState, useMemo, useRef } from "react";
import API_BASE from "../api";
import {
  MapContainer, TileLayer, CircleMarker, Tooltip, Polyline, useMap,
} from "react-leaflet";
import "leaflet/dist/leaflet.css";

const PAGE_SIZE = 20;
const DEFAULT_MIN_POP = 30000;

function completenessColor(ratio) {
  if (ratio >= 0.98) return "#2ecc71";
  if (ratio >= 0.95) return "#f1c40f";
  return "#e74c3c";
}

const TYPE_BORDER = { historical: "#3498db", recent: "#e67e22", both: "#111" };
const TYPE_LABEL  = { historical: "Historical top 3", recent: "Recent top 3", both: "Both" };

function getLast60Days() {
  const days = [];
  for (let i = 59; i >= 0; i--) {
    const d = new Date();
    d.setDate(d.getDate() - i);
    days.push(d.toISOString().split("T")[0]);
  }
  return days;
}

function CompletenessChart({ recentData }) {
  const days = getLast60Days();
  const datesWithData = new Set(recentData.map((r) => r.date));
  const presentCount = days.filter((d) => datesWithData.has(d)).length;
  return (
    <div style={{ padding: "8px 0" }}>
      <div style={styles.chartGrid}>
        {days.map((day) => {
          const hasData = datesWithData.has(day);
          return (
            <div
              key={day}
              title={`${day}: ${hasData ? "data present" : "no data"}`}
              style={{ ...styles.dayCell, background: hasData ? "#2ecc71" : "#e74c3c" }}
            />
          );
        })}
      </div>
      <div style={styles.chartLabels}>
        <span>{days[0]}</span>
        <span style={{ color: "#555", fontSize: 12 }}>{presentCount}/60 days with data</span>
        <span>{days[days.length - 1]}</span>
      </div>
    </div>
  );
}

function MapController({ stations, cities, selectedStationId, selectedCityZip }) {
  const map = useMap();
  useEffect(() => {
    if (selectedCityZip) {
      const city = cities.find((c) => c.zip === selectedCityZip);
      if (city) map.flyTo([city.lat, city.lng], 8, { duration: 1 });
    } else if (selectedStationId) {
      const s = stations.find((s) => s.station_id === selectedStationId);
      if (s) map.flyTo([s.latitude, s.longitude], 8, { duration: 1 });
    }
  }, [selectedStationId, selectedCityZip, stations, cities, map]);
  return null;
}

const STATION_COLS = [
  { key: "station_name",            label: "Station",        filterable: true,  filterHint: "filter…" },
  { key: "state",                   label: "State",          filterable: true,  filterHint: "filter…" },
  { key: "station_id",              label: "ID",             filterable: true,  filterHint: "filter…" },
  { key: "completeness_ratio",      label: "Completeness",   filterable: "min", filterHint: "min %"   },
  { key: "usable_start_year",       label: "Usable From",    filterable: "max", filterHint: "≤ yr"    },
  { key: "current_complete_streak", label: "Streak (yrs)",   filterable: "min", filterHint: "min"     },
  { key: "max_consecutive_sparse_months", label: "Max Gap (mo)", filterable: "max", filterHint: "≤" },
  { key: "num_complete_years",      label: "Complete Yrs",   filterable: "min", filterHint: "min"     },
  { key: "avg_days_per_year",       label: "Avg Days/Yr",    filterable: "min", filterHint: "min"     },
  { key: "first_year",              label: "First Yr",       filterable: "max", filterHint: "≤ yr"    },
  { key: "most_recent_year",        label: "Last Yr",        filterable: "min", filterHint: "≥ yr"    },
  { key: "recent_60d_completeness", label: "Last 60d",       filterable: "min", filterHint: "min %"   },
  { key: "station_type",            label: "Type",           filterable: true,  filterHint: "filter…" },
  { key: "elevation",               label: "Elev (m)",       filterable: "min", filterHint: "min m"   },
];

const CITY_COLS = [
  { key: "city",       label: "City",       filterable: true  },
  { key: "state",      label: "State",      filterable: true  },
  { key: "zip",        label: "ZIP",        filterable: true  },
  { key: "population", label: "Population", filterable: false },
  { key: "station_id", label: "Station ID", filterable: true  },
];

export default function CitiesPage() {
  const [stations, setStations]         = useState([]);
  const [cities, setCities]             = useState([]);
  const [stationsLoading, setStationsLoading] = useState(true);
  const [citiesLoading, setCitiesLoading]     = useState(true);
  const [error, setError]               = useState(null);

  const [minPop, setMinPop]             = useState(DEFAULT_MIN_POP);
  const [minPopInput, setMinPopInput]   = useState(String(DEFAULT_MIN_POP));

  const [activeTab, setActiveTab]       = useState("stations"); // "stations" | "cities"

  const [stationSort, setStationSort]   = useState({ col: "station_name", dir: "asc" });
  const [citySort, setCitySort]         = useState({ col: "city", dir: "asc" });
  const [stationFilters, setStationFilters] = useState({
    station_name: "", state: "", station_id: "", station_type: "",
    completeness_ratio_min: "", usable_start_year_max: "",
    current_complete_streak_min: "", max_consecutive_sparse_months_max: "", num_complete_years_min: "",
    avg_days_per_year_min: "", first_year_max: "", most_recent_year_min: "",
    recent_60d_completeness_min: "", elevation_min: "",
  });
  const [cityFilters, setCityFilters]   = useState({ city: "", state: "", zip: "", station_id: "" });
  const [stationPage, setStationPage]   = useState(1);
  const [cityPage, setCityPage]         = useState(1);

  const [showStations, setShowStations] = useState(true);
  const [showCities, setShowCities]     = useState(true);

  const [selectedStationId, setSelectedStationId] = useState(null);
  const [selectedCityZip, setSelectedCityZip]     = useState(null);
  const [panelOpen, setPanelOpen]       = useState(false);
  const [recentData, setRecentData]       = useState([]);
  const [recentLoading, setRecentLoading] = useState(false);
  const [yearlySummary, setYearlySummary]         = useState([]);
  const [yearlySummaryLoading, setYearlySummaryLoading] = useState(false);

  const selectedStationRowRef = useRef(null);
  const selectedCityRowRef    = useRef(null);

  // Fetch stations
  useEffect(() => {
    fetch(`${API_BASE}/stations`)
      .then((r) => { if (!r.ok) throw new Error("Failed to fetch stations"); return r.json(); })
      .then((d) => { setStations(d); setStationsLoading(false); })
      .catch((e) => { setError(e.message); setStationsLoading(false); });
  }, []);

  // Fetch cities when minPop changes
  useEffect(() => {
    setCitiesLoading(true);
    fetch(`${API_BASE}/cities?min_population=${minPop}`)
      .then((r) => { if (!r.ok) throw new Error("Failed to fetch cities"); return r.json(); })
      .then((d) => { setCities(d); setCitiesLoading(false); })
      .catch((e) => { setError(e.message); setCitiesLoading(false); });
  }, [minPop]);

  // Fetch recent data and yearly summary when station selected
  useEffect(() => {
    if (!selectedStationId) {
      setRecentData([]);
      setYearlySummary([]);
      return;
    }
    setRecentLoading(true);
    fetch(`${API_BASE}/stations/${selectedStationId}/recent`)
      .then((r) => r.json())
      .then((d) => { setRecentData(d); setRecentLoading(false); })
      .catch(() => { setRecentData([]); setRecentLoading(false); });

    setYearlySummaryLoading(true);
    fetch(`${API_BASE}/stations/${selectedStationId}/yearly-summary`)
      .then((r) => r.json())
      .then((d) => { setYearlySummary(d); setYearlySummaryLoading(false); })
      .catch(() => { setYearlySummary([]); setYearlySummaryLoading(false); });
  }, [selectedStationId]);

  // Scroll selected rows into view
  useEffect(() => {
    selectedStationRowRef.current?.scrollIntoView({ block: "nearest", behavior: "smooth" });
  }, [selectedStationId]);
  useEffect(() => {
    selectedCityRowRef.current?.scrollIntoView({ block: "nearest", behavior: "smooth" });
  }, [selectedCityZip]);

  // Reset pages on filter/sort change
  useEffect(() => { setStationPage(1); }, [stationFilters, stationSort]);
  useEffect(() => { setCityPage(1); }, [cityFilters, citySort]);

  function selectStation(id) {
    setSelectedStationId((prev) => (prev === id ? null : id));
    setSelectedCityZip(null);
    setPanelOpen(true);
  }

  function selectCity(zip) {
    const city = cities.find((c) => c.zip === zip);
    setSelectedCityZip((prev) => (prev === zip ? null : zip));
    setSelectedStationId(city?.station_id ?? null);
    setPanelOpen(true);
  }

  function clearSelection() {
    setSelectedStationId(null);
    setSelectedCityZip(null);
    setPanelOpen(false);
  }

  function applyPopFilter() {
    const val = parseInt(minPopInput);
    if (!isNaN(val) && val >= 0) setMinPop(val);
  }

  // Derived data
  const selectedStation = stations.find((s) => s.station_id === selectedStationId);
  const selectedCity    = cities.find((c) => c.zip === selectedCityZip);
  const citiesForStation = selectedStationId
    ? cities.filter((c) => c.station_id === selectedStationId)
    : [];

  // Polyline: city → matched station
  const polyline = useMemo(() => {
    if (!selectedCityZip) return null;
    const city    = cities.find((c) => c.zip === selectedCityZip);
    const station = stations.find((s) => s.station_id === city?.station_id);
    if (!city || !station) return null;
    return [[city.lat, city.lng], [station.latitude, station.longitude]];
  }, [selectedCityZip, cities, stations]);

  // Filtered + sorted stations
  const filteredStations = useMemo(() => {
    const n = (v) => v !== "" ? parseFloat(v) : null;
    const minCompleteness = n(stationFilters.completeness_ratio_min);
    const maxUsableStart  = n(stationFilters.usable_start_year_max);
    const minStreak              = n(stationFilters.current_complete_streak_min);
    const maxSparseMonths        = n(stationFilters.max_consecutive_sparse_months_max);
    const minCompleteYrs  = n(stationFilters.num_complete_years_min);
    const minAvgDays      = n(stationFilters.avg_days_per_year_min);
    const maxFirstYear    = n(stationFilters.first_year_max);
    const minLastYear     = n(stationFilters.most_recent_year_min);
    const minRecent60d    = n(stationFilters.recent_60d_completeness_min);
    const minElevation    = n(stationFilters.elevation_min);
    return stations.filter((s) =>
      s.station_name?.toLowerCase().includes(stationFilters.station_name.toLowerCase()) &&
      s.state?.toLowerCase().includes(stationFilters.state.toLowerCase()) &&
      s.station_id?.toLowerCase().includes(stationFilters.station_id.toLowerCase()) &&
      s.station_type?.toLowerCase().includes(stationFilters.station_type.toLowerCase()) &&
      (minCompleteness === null || s.completeness_ratio * 100 >= minCompleteness) &&
      (maxUsableStart  === null || s.usable_start_year <= maxUsableStart) &&
      (minStreak        === null || s.current_complete_streak >= minStreak) &&
      (maxSparseMonths  === null || s.max_consecutive_sparse_months <= maxSparseMonths) &&
      (minCompleteYrs  === null || s.num_complete_years >= minCompleteYrs) &&
      (minAvgDays      === null || s.avg_days_per_year >= minAvgDays) &&
      (maxFirstYear    === null || s.first_year <= maxFirstYear) &&
      (minLastYear     === null || s.most_recent_year >= minLastYear) &&
      (minRecent60d    === null || s.recent_60d_completeness * 100 >= minRecent60d) &&
      (minElevation    === null || s.elevation >= minElevation)
    );
  }, [stations, stationFilters]);

  const sortedStations = useMemo(() => {
    const { col, dir } = stationSort;
    return [...filteredStations].sort((a, b) => {
      const av = a[col] ?? "", bv = b[col] ?? "";
      const cmp = av < bv ? -1 : av > bv ? 1 : 0;
      return dir === "asc" ? cmp : -cmp;
    });
  }, [filteredStations, stationSort]);

  // Filtered + sorted cities
  const filteredCities = useMemo(() =>
    cities.filter((c) =>
      c.city?.toLowerCase().includes(cityFilters.city.toLowerCase()) &&
      c.state?.toLowerCase().includes(cityFilters.state.toLowerCase()) &&
      String(c.zip ?? "").includes(cityFilters.zip) &&
      String(c.station_id ?? "").toLowerCase().includes(cityFilters.station_id.toLowerCase())
    ), [cities, cityFilters]);

  const sortedCities = useMemo(() => {
    const { col, dir } = citySort;
    return [...filteredCities].sort((a, b) => {
      const av = a[col] ?? "", bv = b[col] ?? "";
      const cmp = av < bv ? -1 : av > bv ? 1 : 0;
      return dir === "asc" ? cmp : -cmp;
    });
  }, [filteredCities, citySort]);

  const stationTotalPages = Math.max(1, Math.ceil(sortedStations.length / PAGE_SIZE));
  const cityTotalPages    = Math.max(1, Math.ceil(sortedCities.length / PAGE_SIZE));
  const paginatedStations = sortedStations.slice((stationPage - 1) * PAGE_SIZE, stationPage * PAGE_SIZE);
  const paginatedCities   = sortedCities.slice((cityPage - 1) * PAGE_SIZE, cityPage * PAGE_SIZE);

  function isLeapYear(y) { return (y % 4 === 0 && y % 100 !== 0) || y % 400 === 0; }

  if (stationsLoading) return <div style={styles.centered}>Loading stations...</div>;
  if (error)           return <div style={styles.centered}>Error: {error}</div>;

  const unmatchedCount = cities.filter((c) => !c.station_id).length;

  return (
    <div style={styles.page}>
      <h1 style={styles.heading}>Cities & Station Coverage</h1>
      <p style={styles.subheading}>
        {stations.length} stations · {cities.length} cities (pop ≥ {minPop.toLocaleString()})
        {unmatchedCount > 0 && <span style={{ color: "#e74c3c" }}> · {unmatchedCount} unmatched</span>}
      </p>

      {/* Population filter */}
      <div style={styles.popFilter}>
        <label style={{ fontSize: 13 }}>Min population:</label>
        <input
          type="number"
          style={styles.popInput}
          value={minPopInput}
          min="0"
          step="5000"
          onChange={(e) => setMinPopInput(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && applyPopFilter()}
        />
        <button style={styles.applyBtn} onClick={applyPopFilter}>Apply</button>
        {citiesLoading && <span style={{ fontSize: 12, color: "#888" }}>Loading...</span>}
      </div>

      <MapContainer center={[39.5, -98.35]} zoom={4} style={styles.map} scrollWheelZoom>
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        <MapController
          stations={stations} cities={cities}
          selectedStationId={selectedStationId} selectedCityZip={selectedCityZip}
        />

        {/* Station markers */}
        {showStations && filteredStations.map((s) => {
          const sel = s.station_id === selectedStationId;
          const assignedToIt = citiesForStation.length > 0 && s.station_id === selectedStationId;
          return (
            <CircleMarker
              key={s.station_id}
              center={[s.latitude, s.longitude]}
              radius={sel || assignedToIt ? 8 : 5}
              pathOptions={{
                fillColor: completenessColor(s.completeness_ratio),
                fillOpacity: sel ? 1 : 0.8,
                color: sel ? "#000" : TYPE_BORDER[s.station_type] ?? "#333",
                weight: sel ? 2.5 : 1.5,
              }}
              eventHandlers={{ click: () => selectStation(s.station_id) }}
            >
              <Tooltip>
                <strong>{s.station_name}</strong><br />
                {s.state} · {s.station_id}<br />
                Completeness: {(s.completeness_ratio * 100).toFixed(1)}%
              </Tooltip>
            </CircleMarker>
          );
        })}

        {/* City markers */}
        {showCities && filteredCities.map((c) => {
          const sel = c.zip === selectedCityZip;
          const assignedHere = c.station_id === selectedStationId && selectedStationId !== null;
          return (
            <CircleMarker
              key={c.zip}
              center={[c.lat, c.lng]}
              radius={sel ? 7 : 3}
              pathOptions={{
                fillColor: c.station_id ? "#3498db" : "#e74c3c",
                fillOpacity: sel || assignedHere ? 1 : 0.6,
                color: sel ? "#000" : assignedHere ? "#f39c12" : "#fff",
                weight: sel || assignedHere ? 2 : 0.5,
              }}
              eventHandlers={{ click: () => selectCity(c.zip) }}
            >
              <Tooltip>
                <strong>{c.city}, {c.state}</strong><br />
                ZIP: {c.zip} · Pop: {c.population?.toLocaleString()}<br />
                {c.station_id ? `Station: ${c.station_id}` : "No station assigned"}
              </Tooltip>
            </CircleMarker>
          );
        })}

        {/* Connection line */}
        {polyline && (
          <Polyline positions={polyline} pathOptions={{ color: "#f39c12", weight: 2, dashArray: "6 4" }} />
        )}
      </MapContainer>

      {/* Legend */}
      <div style={styles.legend}>
        <span style={styles.legendGroup}>
          <strong>Stations — fill:</strong>&nbsp;
          <span style={{ ...styles.dot, background: "#2ecc71" }} /> ≥98%&nbsp;
          <span style={{ ...styles.dot, background: "#f1c40f" }} /> ≥95%&nbsp;
          <span style={{ ...styles.dot, background: "#e74c3c" }} /> &lt;95%
        </span>
        <span style={styles.legendGroup}>
          <strong>Stations — border:</strong>&nbsp;
          {Object.entries(TYPE_BORDER).map(([type, color]) => (
            <span key={type} style={{ marginRight: 8 }}>
              <span style={{ ...styles.dot, background: "transparent", border: `2px solid ${color}` }} />&nbsp;{TYPE_LABEL[type]}
            </span>
          ))}
        </span>
        <span style={styles.legendGroup}>
          <strong>Cities:</strong>&nbsp;
          <span style={{ ...styles.dot, background: "#3498db" }} /> matched&nbsp;
          <span style={{ ...styles.dot, background: "#e74c3c" }} /> unmatched
        </span>
      </div>

      {/* Layer toggles */}
      <div style={styles.toggleRow}>
        <label style={styles.toggleLabel}>
          <input type="checkbox" checked={showStations} onChange={(e) => setShowStations(e.target.checked)} />
          &nbsp;Show stations ({filteredStations.length})
        </label>
        <label style={styles.toggleLabel}>
          <input type="checkbox" checked={showCities} onChange={(e) => setShowCities(e.target.checked)} />
          &nbsp;Show cities ({filteredCities.length})
        </label>
      </div>

      {/* Sliding detail panel */}
      <div style={{ ...styles.panel, maxHeight: panelOpen ? 340 : 0 }}>
        {(selectedStation || selectedCity) && (
          <div style={styles.panelInner}>
            <div style={styles.panelHeader}>
              <div>
                {selectedCity && (
                  <>
                    <strong>{selectedCity.city}, {selectedCity.state}</strong>
                    <span style={styles.panelMeta}>
                      ZIP: {selectedCity.zip} · Pop: {selectedCity.population?.toLocaleString()}
                    </span>
                    {selectedCity.station_id
                      ? <span style={styles.panelMeta}>→ Station: {selectedCity.station_id}</span>
                      : <span style={{ ...styles.panelMeta, color: "#e74c3c" }}>No station assigned</span>
                    }
                  </>
                )}
                {selectedStation && !selectedCity && (
                  <>
                    <strong>{selectedStation.station_name}</strong>
                    <span style={styles.panelMeta}>
                      {selectedStation.state} · {selectedStation.station_id} · {citiesForStation.length} cities assigned
                    </span>
                  </>
                )}
              </div>
              <button style={styles.closeBtn} onClick={clearSelection}>✕</button>
            </div>

            {selectedStation && (
              recentLoading
                ? <div style={styles.centered}>Loading...</div>
                : <CompletenessChart recentData={recentData} />
            )}
          </div>
        )}
      </div>

      {/* Yearly summary table */}
      {selectedStationId && (
        <div style={styles.yearlySummarySection}>
          <div style={styles.yearlySummaryHeader}>
            <strong>Annual TMAX coverage — {selectedStation?.station_name ?? selectedStationId}</strong>
            <span style={{ color: "#888", fontSize: 12, marginLeft: 12 }}>
              {yearlySummary.length} years on record
            </span>
          </div>
          {yearlySummaryLoading ? (
            <div style={styles.centered}>Loading...</div>
          ) : (
            <div style={styles.yearlySummaryTableWrapper}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>Year</th>
                    <th style={styles.th}>Days Present</th>
                    <th style={styles.th}>Completeness</th>
                    <th style={styles.th}>First Date</th>
                    <th style={styles.th}>Last Date</th>
                  </tr>
                </thead>
                <tbody>
                  {yearlySummary.map((row, i) => {
                    const expected = isLeapYear(row.year) ? 366 : 365;
                    const pct = row.days_present / expected;
                    return (
                      <tr key={row.year} style={i % 2 === 0 ? styles.rowEven : {}}>
                        <td style={{ ...styles.td, fontWeight: 600 }}>{row.year}</td>
                        <td style={styles.td}>{row.days_present} / {expected}</td>
                        <td style={styles.td}>
                          <span style={{ ...styles.badge, background: completenessColor(pct) }}>
                            {(pct * 100).toFixed(1)}%
                          </span>
                        </td>
                        <td style={{ ...styles.td, fontFamily: "monospace" }}>{row.min_date}</td>
                        <td style={{ ...styles.td, fontFamily: "monospace" }}>{row.max_date}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Table tabs */}
      <div style={styles.tabs}>
        <button
          style={{ ...styles.tab, ...(activeTab === "stations" ? styles.tabActive : {}) }}
          onClick={() => setActiveTab("stations")}
        >
          Stations ({stations.length})
        </button>
        <button
          style={{ ...styles.tab, ...(activeTab === "cities" ? styles.tabActive : {}) }}
          onClick={() => setActiveTab("cities")}
        >
          Cities ({cities.length})
          {unmatchedCount > 0 && <span style={styles.unmatchedBadge}>{unmatchedCount} unmatched</span>}
        </button>
      </div>

      {/* Stations table */}
      {activeTab === "stations" && (
        <>
          <div style={styles.controls}>
            <span style={styles.count}>{sortedStations.length} stations · page {stationPage} of {stationTotalPages}</span>
          </div>
          <div style={styles.tableWrapper}>
            <table style={styles.table}>
              <thead>
                <tr>
                  {STATION_COLS.map(({ key, label }) => (
                    <th key={key} style={styles.th} onClick={() => setStationSort(s => ({ col: key, dir: s.col === key && s.dir === "asc" ? "desc" : "asc" }))}>
                      {label}{stationSort.col === key ? (stationSort.dir === "asc" ? " ↑" : " ↓") : " ↕"}
                    </th>
                  ))}
                </tr>
                <tr>
                  {STATION_COLS.map(({ key, filterable, filterHint }) => {
                    const filterKey = filterable === "min" ? key + "_min" : filterable === "max" ? key + "_max" : key;
                    return (
                      <th key={key} style={styles.filterCell}>
                        {filterable === true && (
                          <input style={styles.filterInput} placeholder={filterHint ?? "filter…"}
                            value={stationFilters[filterKey] ?? ""}
                            onChange={(e) => setStationFilters((f) => ({ ...f, [filterKey]: e.target.value }))} />
                        )}
                        {(filterable === "min" || filterable === "max") && (
                          <input style={styles.filterInput} placeholder={filterHint ?? (filterable === "min" ? "≥" : "≤")} type="number"
                            value={stationFilters[filterKey] ?? ""}
                            onChange={(e) => setStationFilters((f) => ({ ...f, [filterKey]: e.target.value }))} />
                        )}
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {paginatedStations.map((s, i) => {
                  const sel = s.station_id === selectedStationId;
                  return (
                    <tr key={s.station_id} ref={sel ? selectedStationRowRef : null}
                      style={{ ...(sel ? styles.rowSelected : i % 2 === 0 ? styles.rowEven : {}), cursor: "pointer" }}
                      onClick={() => selectStation(s.station_id)}>
                      <td style={styles.td}>{s.station_name}</td>
                      <td style={styles.td}>{s.state}</td>
                      <td style={{ ...styles.td, fontFamily: "monospace" }}>{s.station_id}</td>
                      <td style={styles.td}><span style={{ ...styles.badge, background: completenessColor(s.completeness_ratio) }}>{(s.completeness_ratio * 100).toFixed(1)}%</span></td>
                      <td style={styles.td}>{s.usable_start_year ?? "—"}</td>
                      <td style={styles.td}>{s.current_complete_streak ?? "—"}</td>
                      <td style={styles.td}>{s.max_consecutive_sparse_months ?? "—"}</td>
                      <td style={styles.td}>{s.num_complete_years}</td>
                      <td style={styles.td}>{s.avg_days_per_year}</td>
                      <td style={styles.td}>{s.first_year}</td>
                      <td style={styles.td}>{s.most_recent_year}</td>
                      <td style={styles.td}><span style={{ ...styles.badge, background: completenessColor(s.recent_60d_completeness) }}>{(s.recent_60d_completeness * 100).toFixed(0)}%</span></td>
                      <td style={styles.td}><span style={{ ...styles.badge, background: TYPE_BORDER[s.station_type] ?? "#ccc", color: "#fff" }}>{s.station_type}</span></td>
                      <td style={styles.td}>{s.elevation != null ? s.elevation.toFixed(0) : "—"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <Pagination page={stationPage} total={stationTotalPages} onChange={setStationPage} />
        </>
      )}

      {/* Cities table */}
      {activeTab === "cities" && (
        <>
          <div style={styles.controls}>
            <span style={styles.count}>{sortedCities.length} cities · page {cityPage} of {cityTotalPages}</span>
          </div>
          <div style={styles.tableWrapper}>
            <table style={styles.table}>
              <thead>
                <tr>
                  {CITY_COLS.map(({ key, label }) => (
                    <th key={key} style={styles.th} onClick={() => setCitySort(s => ({ col: key, dir: s.col === key && s.dir === "asc" ? "desc" : "asc" }))}>
                      {label}{citySort.col === key ? (citySort.dir === "asc" ? " ↑" : " ↓") : " ↕"}
                    </th>
                  ))}
                </tr>
                <tr>
                  {CITY_COLS.map(({ key, filterable }) => (
                    <th key={key} style={styles.filterCell}>
                      {filterable && (
                        <input style={styles.filterInput} placeholder="filter…"
                          value={cityFilters[key] ?? ""}
                          onChange={(e) => setCityFilters((f) => ({ ...f, [key]: e.target.value }))} />
                      )}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {paginatedCities.map((c, i) => {
                  const sel = c.zip === selectedCityZip;
                  return (
                    <tr key={c.zip} ref={sel ? selectedCityRowRef : null}
                      style={{ ...(sel ? styles.rowSelected : i % 2 === 0 ? styles.rowEven : {}), cursor: "pointer" }}
                      onClick={() => selectCity(c.zip)}>
                      <td style={styles.td}>{c.city}</td>
                      <td style={styles.td}>{c.state}</td>
                      <td style={{ ...styles.td, fontFamily: "monospace" }}>{String(c.zip ?? "").padStart(5, "0")}</td>
                      <td style={styles.td}>{c.population?.toLocaleString() ?? "—"}</td>
                      <td style={styles.td}>
                        {c.station_id
                          ? <span style={{ fontFamily: "monospace" }}>{c.station_id}</span>
                          : <span style={{ color: "#e74c3c", fontWeight: 600 }}>none</span>
                        }
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <Pagination page={cityPage} total={cityTotalPages} onChange={setCityPage} />
        </>
      )}
    </div>
  );
}

function Pagination({ page, total, onChange }) {
  return (
    <div style={styles.pagination}>
      <button style={styles.pageBtn} disabled={page === 1} onClick={() => onChange(1)}>«</button>
      <button style={styles.pageBtn} disabled={page === 1} onClick={() => onChange((p) => p - 1)}>‹</button>
      <span style={styles.pageInfo}>Page {page} of {total}</span>
      <button style={styles.pageBtn} disabled={page === total} onClick={() => onChange((p) => p + 1)}>›</button>
      <button style={styles.pageBtn} disabled={page === total} onClick={() => onChange(total)}>»</button>
    </div>
  );
}

const styles = {
  page:            { fontFamily: "sans-serif", maxWidth: 1800, margin: "0 auto", padding: "24px 16px", background: "#fff", color: "#111", minHeight: "100vh" },
  heading:         { margin: 0, color: "#111" },
  subheading:      { color: "#555", marginTop: 4, marginBottom: 12 },
  popFilter:       { display: "flex", alignItems: "center", gap: 8, marginBottom: 16 },
  popInput:        { width: 110, padding: "4px 8px", fontSize: 13, border: "1px solid #ccc", borderRadius: 4, background: "#fff", color: "#111" },
  applyBtn:        { padding: "4px 12px", fontSize: 13, border: "1px solid #999", borderRadius: 4, cursor: "pointer", background: "#f5f5f5", color: "#111" },
  map:             { height: 500, width: "100%", borderRadius: 8, marginBottom: 8 },
  legend:          { display: "flex", flexWrap: "wrap", gap: 16, fontSize: 12, color: "#444", marginBottom: 8 },
  legendGroup:     { display: "flex", alignItems: "center", gap: 4 },
  dot:             { display: "inline-block", width: 10, height: 10, borderRadius: "50%" },
  panel:           { overflow: "hidden", transition: "max-height 0.35s ease", background: "#f4f8ff", border: "1px solid #c5d8f5", borderRadius: 8, marginBottom: 12, color: "#111" },
  panelInner:      { padding: "12px 16px" },
  panelHeader:     { display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 8 },
  panelMeta:       { marginLeft: 12, color: "#555", fontSize: 13 },
  closeBtn:        { background: "none", border: "none", fontSize: 16, cursor: "pointer", color: "#666" },
  tabs:            { display: "flex", gap: 0, marginBottom: 0, borderBottom: "2px solid #ddd" },
  tab:             { padding: "8px 20px", cursor: "pointer", border: "none", background: "none", fontSize: 14, color: "#555", borderBottom: "2px solid transparent", marginBottom: -2 },
  tabActive:       { color: "#3498db", borderBottom: "2px solid #3498db", fontWeight: 600 },
  unmatchedBadge:  { marginLeft: 8, background: "#e74c3c", color: "#fff", borderRadius: 10, padding: "1px 7px", fontSize: 11 },
  controls:        { display: "flex", alignItems: "center", gap: 16, margin: "10px 0 8px" },
  count:           { color: "#555", fontSize: 13 },
  tableWrapper:    { overflowX: "auto" },
  table:           { width: "100%", borderCollapse: "collapse", fontSize: 13, background: "#fff" },
  th:              { textAlign: "left", padding: "8px 10px", borderBottom: "2px solid #ddd", cursor: "pointer", whiteSpace: "nowrap", userSelect: "none", background: "#fff", color: "#222" },
  filterCell:      { padding: "4px 6px", borderBottom: "2px solid #ddd", background: "#fff" },
  filterInput:     { width: "100%", padding: "3px 6px", fontSize: 12, border: "1px solid #ccc", borderRadius: 4, boxSizing: "border-box", background: "#fff", color: "#111" },
  td:              { padding: "6px 10px", borderBottom: "1px solid #eee", color: "#111", background: "#fff" },
  rowEven:         { background: "#f2f4f7" },
  rowSelected:     { background: "#d0eaff", outline: "2px solid #3498db" },
  badge:           { display: "inline-block", padding: "2px 6px", borderRadius: 4, fontSize: 12, fontWeight: 600, color: "#111" },
  chartGrid:       { display: "flex", gap: 4 },
  dayCell:         { flex: 1, height: 28, borderRadius: 3, minWidth: 0 },
  chartLabels:     { display: "flex", justifyContent: "space-between", marginTop: 6, fontSize: 11, color: "#888" },
  pagination:      { display: "flex", alignItems: "center", gap: 8, marginTop: 12, justifyContent: "center" },
  pageBtn:         { padding: "4px 10px", cursor: "pointer", border: "1px solid #ccc", borderRadius: 4, background: "#fff", color: "#111", fontSize: 14 },
  pageInfo:        { fontSize: 13, color: "#555" },
  yearlySummarySection:      { border: "1px solid #dde3ed", borderRadius: 8, marginBottom: 14, background: "#fff" },
  yearlySummaryHeader:       { padding: "10px 14px", borderBottom: "1px solid #eee", display: "flex", alignItems: "center" },
  yearlySummaryTableWrapper: { maxHeight: 320, overflowY: "auto", overflowX: "auto" },
  centered:        { padding: 40, textAlign: "center", color: "#111" },
  toggleRow:       { display: "flex", gap: 20, marginBottom: 10, fontSize: 13, color: "#111" },
  toggleLabel:     { display: "flex", alignItems: "center", cursor: "pointer", userSelect: "none" },
};
